import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Badge, Spinner, Flex, Icon, Button, IconButton,
  useColorModeValue, useToast, Input, Textarea, Select, FormControl, FormLabel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, Switch,
} from '@chakra-ui/react';
import { FaSync, FaPlus, FaMapMarkerAlt, FaHome, FaEdit, FaTrash } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const KIND_META = {
  dropoff: { label: 'Drop-off location', icon: FaMapMarkerAlt, color: 'blue', hint: 'Phleb drives here and drops the batch off.' },
  pickup:  { label: 'Home pickup',       icon: FaHome,         color: 'purple', hint: "Courier picks up from the phleb's own address." },
};

const emptyForm = { id: null, name: '', kind: 'dropoff', address: '', notes: '', active: true };

// Admin management of reusable drop spots for spun specimens.
function DropSpots() {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('drop_spots')
        .select('id, name, kind, address, notes, active, created_at')
        .order('active', { ascending: false })
        .order('name', { ascending: true });
      if (error) throw error;
      setSpots(data || []);
    } catch (err) {
      console.error('Drop spots fetch error:', err);
      toast({ title: 'Could not load drop spots', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setForm(emptyForm); onOpen(); };
  const openEdit = (s) => { setForm({ id: s.id, name: s.name, kind: s.kind, address: s.address || '', notes: s.notes || '', active: s.active }); onOpen(); };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', status: 'warning', duration: 2500 }); return; }
    if (form.kind === 'dropoff' && !form.address.trim()) { toast({ title: 'Drop-off spots need an address', status: 'warning', duration: 2500 }); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        address: form.kind === 'dropoff' ? form.address.trim() : null,
        notes: form.notes.trim() || null,
        active: form.active,
      };
      if (form.id) {
        const { error } = await supabase.from('drop_spots').update(payload).eq('id', form.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('drop_spots').insert(payload);
        if (error) throw error;
      }
      toast({ title: form.id ? 'Drop spot updated' : 'Drop spot added', status: 'success', duration: 2000 });
      onClose();
      fetchData();
    } catch (err) {
      toast({ title: 'Could not save', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      const { error } = await supabase.from('drop_spots').update({ active: !s.active }).eq('id', s.id);
      if (error) throw error;
      setSpots((prev) => prev.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x)));
    } catch (err) {
      toast({ title: 'Could not update', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete drop spot "${s.name}"? Specimens already sent there keep their saved destination.`)) return;
    try {
      const { error } = await supabase.from('drop_spots').delete().eq('id', s.id);
      if (error) throw error;
      setSpots((prev) => prev.filter((x) => x.id !== s.id));
      toast({ title: 'Drop spot deleted', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Could not delete', description: err.message, status: 'error', duration: 4000 });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Heading size="lg" mb={1}>Drop Spots</Heading>
          <Text color={mutedColor} fontSize="sm">
            Reusable destinations for spun specimens — a drop-off location, or a pickup from the phleb's own address.
          </Text>
        </Box>
        <HStack>
          <Button size="sm" variant="outline" leftIcon={<Icon as={FaSync} />} onClick={fetchData} isLoading={loading}>Refresh</Button>
          <Button size="sm" colorScheme="blue" leftIcon={<Icon as={FaPlus} />} onClick={openNew}>New spot</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : spots.length === 0 ? (
        <Text color={mutedColor}>No drop spots yet. Add one with “New spot.”</Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {spots.map((s) => {
            const meta = KIND_META[s.kind] || KIND_META.dropoff;
            return (
              <Flex key={s.id} bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={3}
                align="center" gap={3} opacity={s.active ? 1 : 0.55}>
                <Icon as={meta.icon} color={`${meta.color}.500`} boxSize={5} flexShrink={0} />
                <Box flex={1} minW={0}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontWeight="700" noOfLines={1}>{s.name}</Text>
                    <Badge colorScheme={meta.color}>{meta.label}</Badge>
                    {!s.active && <Badge colorScheme="gray">Inactive</Badge>}
                  </HStack>
                  <Text fontSize="sm" color={mutedColor} noOfLines={1}>
                    {s.kind === 'dropoff' ? (s.address || '—') : "Pickup from the phleb's saved address"}
                  </Text>
                  {s.notes && <Text fontSize="xs" color={mutedColor} noOfLines={1}>{s.notes}</Text>}
                </Box>
                <HStack spacing={1} flexShrink={0}>
                  <Switch size="sm" isChecked={s.active} onChange={() => toggleActive(s)} title={s.active ? 'Active' : 'Inactive'} />
                  <IconButton size="sm" variant="ghost" aria-label="Edit" icon={<FaEdit />} onClick={() => openEdit(s)} />
                  <IconButton size="sm" variant="ghost" colorScheme="red" aria-label="Delete" icon={<FaTrash />} onClick={() => remove(s)} />
                </HStack>
              </Flex>
            );
          })}
        </VStack>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>{form.id ? 'Edit drop spot' : 'New drop spot'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Quest Depot — Midtown" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Kind</FormLabel>
                <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="dropoff">Drop-off location (phleb drives here)</option>
                  <option value="pickup">Home pickup (courier collects from the phleb)</option>
                </Select>
                <Text fontSize="xs" color={mutedColor} mt={1}>{(KIND_META[form.kind] || {}).hint}</Text>
              </FormControl>
              {form.kind === 'dropoff' && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Address</FormLabel>
                  <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city, state, ZIP" />
                </FormControl>
              )}
              <FormControl>
                <FormLabel fontSize="sm">Notes (optional)</FormLabel>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Hours, contact, instructions…" />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <Switch id="active" isChecked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} mr={2} />
                <FormLabel htmlFor="active" fontSize="sm" mb={0}>Active (selectable when assigning)</FormLabel>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={save} isLoading={saving}>{form.id ? 'Save' : 'Add spot'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default DropSpots;
